import React, { createContext, useContext, useEffect, useSyncExternalStore } from 'react';
import { EngineerNowProvider, useSession } from '@engineernow/convex-client/react';
import { SESSION_EXPIRED_EVENT } from '@engineernow/convex-client';
import { getEngineerNowClient, isAdminSession } from '../convex/client';

interface AuthContextType {
    isAuthenticated: boolean;
    logout: () => void;
    authenticate: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const clearCookies = () => {
    document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
};

// Сессия админа — cookies token.access / token.refresh из клиентского пакета (вход через
// POST /admin/login); состояние входа следует за cookies (и между вкладками).
const SessionAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const session = useSession();
    const isAuthenticated = useSyncExternalStore(session.subscribe, isAdminSession, () => false);

    const logout = async () => {
        await session.logout();
        clearCookies();
        window.location.href = '/';
    };

    // Сессию не удалось продлить или функция ответила 401/403 — выход, как раньше.
    useEffect(() => {
        const onExpired = () => void logout();
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session]);

    // Вход сам обновляет состояние (cookies + событие сессии).
    const authenticate = () => {};

    return (
        <AuthContext.Provider value={{ isAuthenticated, logout, authenticate }}>
            {children}
        </AuthContext.Provider>
    );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <EngineerNowProvider client={getEngineerNowClient()}>
        <SessionAuth>{children}</SessionAuth>
    </EngineerNowProvider>
);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
